/**
 * Route Card Component
 * 
 * Reusable route card component for displaying route information in both
 * Atlas and database panels. Supports expandable accordion details,
 * selection, and validation/match status display.
 * 
 * @module RouteCard
 */

import React, { useState, Fragment } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  LocationOn,
  ExpandMore,
  AccessTime,
  Route as RouteIcon,
  DirectionsBus,
  WorkOutline,
  CalendarMonth,
} from '@mui/icons-material';
import type { FixedRoute, FixedRouteStop } from '../../../../types/fixedRoutes';
import type { UATRoute } from '../../../../services/fixedRouteUatClient';
import { useFixedRoutes } from '../../../../context/FixedRoutesContext';
import { describeServiceDays } from '../../../fixedRoutes/attachOperations/draftPlan';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
/**
 * Validation status for upload operations
 */
export interface ValidationStatus {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  coordinateMatches?: {
    matched: number;
    unmatched: number;
    ambiguous: number;
  };
  stopChanges?: {
    added: number;
    removed: number;
    reordered: number;
    modified: number;
  };
  versionConflict?: {
    exists: boolean;
    versionName?: string;
  };
}

/**
 * Match status for download operations
 */
export interface MatchStatus {
  existsInAtlas: boolean;
  existingRouteName?: string;
  isDuplicate: boolean;
  importPreview?: {
    willCreate: boolean;
    willUpdate: boolean;
  };
}

/**
 * Props for RouteCard component
 */
export interface RouteCardProps {
  /** Route data - either FixedRoute (Atlas) or UATRoute (database) */
  route: FixedRoute | UATRoute;
  /** Source of the route ('atlas' or 'database') */
  source: 'atlas' | 'database';
  /** Whether the route is selected */
  selected: boolean;
  /** Callback when selection changes */
  onSelect: (selected: boolean) => void;
  /** Whether the accordion is expanded (optional, controlled) */
  expanded?: boolean;
  /** Callback when expansion changes (optional, for controlled mode) */
  onExpand?: (expanded: boolean) => void;
  /** Validation status for upload operations (optional) */
  validationStatus?: ValidationStatus;
  /** Match status for download operations (optional) */
  matchStatus?: MatchStatus;
}

/**
 * RouteCard Component
 * 
 * Displays route information in a card format with expandable accordion details.
 * Supports both Atlas routes (FixedRoute) and database routes (UATRoute).
 */
export const RouteCard: React.FC<RouteCardProps> = React.memo(({
  route,
  source,
  selected,
  onSelect,
  expanded: controlledExpanded,
  onExpand,
  validationStatus,
  matchStatus,
}) => {
  const { t } = useTranslation('mapTransfer');
  const { stops: allStops } = useFixedRoutes();
  
  // Internal state for expanded if not controlled
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const handleExpandChange = (newExpanded: boolean) => {
    if (onExpand) {
      onExpand(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  // Extract route information based on source
  const isAtlas = source === 'atlas';
  const fixedRoute = isAtlas ? route as FixedRoute : null;
  const uatRoute = !isAtlas ? route as UATRoute : null;
  
  // Look up full stop information for Atlas routes
  const getStopInfo = (stopId: string): FixedRouteStop | undefined => {
    return allStops.find((s) => s.id === stopId);
  };

  // Route name
  const routeName = fixedRoute?.name || uatRoute?.label || t('routes.card.unnamed');
  
  // Route ID
  const routeId = route.id;
  const displayRouteId = routeId.length > 20 ? `${routeId.substring(0, 20)}...` : routeId;

  // Stop count - prefer routeStops.next.length (most accurate), fall back to stops.length or route's stopCount
  const stopCount = fixedRoute?.stops?.length 
    || uatRoute?.routeStops?.next?.length 
    || uatRoute?.stops?.length 
    || 0;

  // Version label - preserve import/download metadata on Atlas routes
  // and show the database version when available.
  const routeVersion = fixedRoute?.shortName || uatRoute?.version;

  // Start time (Atlas only)
  const startTime = fixedRoute?.startTime;

  // Route color (Atlas or database)
  const routeColor = fixedRoute?.color || uatRoute?.colour;

  // Version name (database only - would need to be passed separately or extracted from route)
  // For now, we'll skip this as it's not directly in UATRoute interface

  return (
    <Card
      sx={{
        marginBottom: 1,
        elevation: selected ? 3 : 1,
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: selected ? 'action.selected' : 'background.paper',
        '&:hover': {
          elevation: 2,
          backgroundColor: 'action.hover',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ padding: 2 }}>
        {/* Card Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            marginBottom: 1,
          }}
        >
          {/* Checkbox */}
          <Checkbox
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />

          {/* Route Name and Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              component="h3"
              sx={{
                fontWeight: 500,
                marginBottom: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {routeName}
            </Typography>

            {/* Metadata Row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              {/* Route ID */}
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                }}
              >
                {t('routes.card.idLabel', { value: displayRouteId })}
              </Typography>

              {/* Stop Count */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Icon name="location-dot" size={20} />
                <Typography variant="caption" color="text.secondary">
                  {/* DB routes load stops lazily on expand, so the count is only
                      known once stopCount > 0 — avoid showing a misleading "0 stops". */}
                  {stopCount > 0 ? t('routes.card.stopsCount', { count: stopCount }) : '—'}
                </Typography>
              </Box>

              {/* Start Time (Atlas only) */}
              {startTime && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Icon name="clock" size={20} />
                  <Typography variant="caption" color="text.secondary">
                    {startTime}
                  </Typography>
                </Box>
              )}

              {/* Route Color Indicator */}
              {routeColor && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: routeColor,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  title={t('routes.card.routeColorTitle', { color: routeColor })}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Accordion for Details */}
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => handleExpandChange(isExpanded)}
          sx={{
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            marginTop: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<Icon size={20} name="chevron-down" />}
            sx={{
              padding: 0,
              minHeight: 32,
              cursor: 'pointer',
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {expanded
                ? t('routes.card.hideDetails')
                : routeVersion
                  ? stopCount > 0
                    ? t('routes.card.viewDetailsWithVersion', { count: stopCount, version: routeVersion })
                    : t('routes.card.viewDetailsWithVersionGeneric', { version: routeVersion })
                  : stopCount > 0
                    ? t('routes.card.viewDetails', { count: stopCount })
                    : t('routes.card.viewDetailsGeneric')}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ padding: 0, paddingTop: 1 }}>
            <Divider sx={{ marginBottom: 1 }} />

            {/* Stop List */}
            {stopCount > 0 ? (
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {isAtlas && fixedRoute?.stops?.map((stopConfig, index) => {
                  const stopInfo = getStopInfo(stopConfig.stopId);
                  const secondaryParts: React.ReactNode[] = [];
                  
                  if (stopInfo?.coordinates) {
                    secondaryParts.push(
                      <React.Fragment key="coords">
                        {t('routes.card.latLng', { lat: stopInfo.coordinates.lat.toFixed(4), lng: stopInfo.coordinates.lng.toFixed(4) })}
                        <br />
                      </React.Fragment>
                    );
                  }

                  if (stopInfo?.address) {
                    secondaryParts.push(
                      <React.Fragment key="address">
                        {stopInfo.address}
                        <br />
                      </React.Fragment>
                    );
                  }

                  secondaryParts.push(
                    <React.Fragment key="wait">
                      {t('routes.card.waitTimeMinutes', { value: stopConfig.waitTimeMinutes })}
                    </React.Fragment>
                  );

                  if (stopInfo?.label && stopInfo.label !== stopInfo.name) {
                    secondaryParts.push(
                      <React.Fragment key="label">
                        <br />
                        {t('routes.card.labelLine', { value: stopInfo.label })}
                      </React.Fragment>
                    );
                  }

                  return (
                    <ListItem key={`${stopConfig.stopId}-${index}`} sx={{ paddingX: 1 }}>
                      <ListItemText
                        primary={t('routes.card.stopPrefix', { index: index + 1, name: stopInfo?.name || stopInfo?.label || t('routes.card.stopFallback', { value: stopConfig.stopId }) })}
                        secondary={secondaryParts}
                      />
                    </ListItem>
                  );
                })}
                {!isAtlas && uatRoute?.routeStops?.next?.map((stop, index) => {
                  const secondaryParts: React.ReactNode[] = [];
                  
                  if (stop.coordinates) {
                    secondaryParts.push(
                      <React.Fragment key="coords">
                        {t('routes.card.latLng', { lat: stop.coordinates.lat.toFixed(4), lng: stop.coordinates.lng.toFixed(4) })}
                        <br />
                      </React.Fragment>
                    );
                  }

                  if (stop.order !== undefined) {
                    secondaryParts.push(
                      <React.Fragment key="order">
                        {t('routes.card.orderLine', { value: stop.order })}
                        <br />
                      </React.Fragment>
                    );
                  }

                  if (stop.expectedArrivalTime) {
                    secondaryParts.push(
                      <React.Fragment key="arrival">
                        {t('routes.card.arrivalLine', { value: stop.expectedArrivalTime })}
                      </React.Fragment>
                    );
                  }

                  return (
                    <ListItem key={`${stop.id || 'stop'}-${index}`} sx={{ paddingX: 1 }}>
                      <ListItemText
                        primary={t('routes.card.stopPrefix', { index: index + 1, name: stop.name || stop.id })}
                        secondary={secondaryParts.length > 0 ? secondaryParts : undefined}
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ padding: 1 }}>
                {t('routes.card.noStops')}
              </Typography>
            )}

            {/* Route Metadata */}
            <Box sx={{ marginTop: 2, paddingTop: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 0.5 }}>
                {t('routes.card.routeIdFull', { value: routeId })}
              </Typography>
              {source === 'database' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('routes.card.sourceLine', { source })}
                </Typography>
              )}
            </Box>

            {/* Vehicles & Operations — the assigned plan (FixedRoute.operationAssignments),
                composed in the Attach Vehicles & Operations modal. Atlas-only. */}
            {isAtlas && !!fixedRoute?.operationAssignments?.length && (
              <Box sx={{ marginTop: 2, paddingTop: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 1 }}
                >
                  <DirectionsBus fontSize="small" />
                  {t('routes.card.vehiclesOperationsTitle', 'Vehicles & Operations')}
                </Typography>
                {fixedRoute.operationAssignments.map((op, opIdx) => (
                  <Box key={op.operationSolutionId || opIdx} sx={{ marginBottom: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <WorkOutline fontSize="inherit" />
                      {op.operationName || op.operationSolutionId || t('routes.card.operationFallback', 'Operation (assign at upload)')}
                      {op.dateRange ? ` · ${op.dateRange.startDate} → ${op.dateRange.endDate}` : ''}
                    </Typography>
                    <List dense disablePadding>
                      {op.vehicleAssignments.map((va, vi) => (
                        <ListItem key={`${va.opsVehicleId}-${vi}`} sx={{ paddingX: 1, paddingY: 0.25 }}>
                          <ListItemText
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DirectionsBus fontSize="inherit" />
                                {va.opsVehicleLabel || va.opsVehicleId || t('routes.card.unmappedVehicle', 'Unmapped vehicle')}
                              </Box>
                            }
                            secondary={
                              <Fragment>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarMonth fontSize="inherit" />
                                  {va.serviceDays ? describeServiceDays(va.serviceDays) : '—'}
                                  {` · ${va.startTime || '—'}–${va.endTime || '—'}`}
                                </Box>
                                <br />
                                {`${va.direction} · ${va.shiftType}`}
                                {va.numberOfRepeats ? ` · ${va.numberOfRepeats} repeats` : ''}
                              </Fragment>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            )}

            {/* Validation Status (Upload Operations) */}
            {validationStatus && (
              <Box sx={{ marginTop: 2, paddingTop: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                  {t('routes.card.validationStatus')}
                </Typography>
                {validationStatus.isValid ? (
                  <Chip label={t('routes.card.valid')} color="success" size="small" />
                ) : (
                  <Chip label={t('routes.card.hasErrors')} color="error" size="small" />
                )}
                {validationStatus.errors.length > 0 && (
                  <Box sx={{ marginTop: 1 }}>
                    {validationStatus.errors.map((error, index) => (
                      <Typography key={index} variant="caption" color="error" sx={{ display: 'block' }}>
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Match Status (Download Operations) */}
            {matchStatus && (
              <Box sx={{ marginTop: 2, paddingTop: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                  {t('routes.card.matchStatus')}
                </Typography>
                {matchStatus.existsInAtlas && (
                  <Chip
                    label={t('routes.card.existsLabel', { value: matchStatus.existingRouteName || t('routes.card.unknownValue') })}
                    color="warning"
                    size="small"
                    sx={{ marginRight: 1 }}
                  />
                )}
                {matchStatus.isDuplicate && (
                  <Chip label={t('routes.card.duplicate')} color="warning" size="small" />
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
});

RouteCard.displayName = 'RouteCard';
